// Pattern Middle-Mile Shipping Fee Estimation
// Origin: Hebron, KY only
// Rates are per cubic foot, USD

// FC code → Pattern rate zone
const FC_TO_ZONE: Record<string, string> = {
  // Central
  FTW5: 'Central', SAT4: 'Central', HOU8: 'Central', FTW1: 'Central',
  SAT1: 'Central', AMA1: 'Central', IAH3: 'Central', DFW6: 'Central',
  OKC2: 'Central',
  // East1
  DET2: 'East1', DET1: 'East1', IND5: 'East1', CMH2: 'East1',
  PIT2: 'East1', AKR1: 'East1', MQJ2: 'East1', CMH7: 'East1',
  IND2: 'East1', LAN2: 'East1', CMH3: 'East1', MQJ1: 'East1',
  PPO4: 'East1', FWA4: 'East1', IND9: 'East1',
  // East2
  TEB3: 'East2', BOS7: 'East2', JVL1: 'East2', TEB6: 'East2',
  LFT1: 'East2', LIT2: 'East2', GSO1: 'East2', GSP1: 'East2',
  MDW2: 'East2', MDT1: 'East2', XLX6: 'East2', MDW9: 'East2',
  RDU4: 'East2', CLT6: 'East2', BWI1: 'East2', ORF2: 'East2',
  RYY2: 'East2', BNA3: 'East2', IGQ2: 'East2', ALB1: 'East2',
  HGR2: 'East2', HSV1: 'East2', AVP9: 'East2', HGR6: 'East2',
  SAV3: 'East2', STL3: 'East2', CHA2: 'East2', BNA2: 'East2',
  SWF2: 'East2', RDU2: 'East2', XRI3: 'East2', ABE8: 'East2',
  MEM1: 'East2', ACY2: 'East2', LBE1: 'East2', TEB9: 'East2',
  ILG1: 'East2', MKC4: 'East2', BDL6: 'East2', RFD2: 'East2',
  RMN3: 'East2', FOE1: 'East2', RIC1: 'East2', PHL6: 'East2',
  XLX1: 'East2', CHO1: 'East2', MDW6: 'East2', BNA6: 'East2',
  MEM2: 'East2', CLT3: 'East2', CLT2: 'East2', STL4: 'East2',
  HIA1: 'East2', MGE3: 'East2', PHL4: 'East2', MDT4: 'East2',
  MEM6: 'East2', AVP1: 'East2', WBW2: 'East2', ABE4: 'East2',
  RFD4: 'East2', BWI4: 'East2', SWF1: 'East2', DCA6: 'East2',
  ORD2: 'East2',
  // Florida (uses E3 rates)
  MCO2: 'Florida', MLB1: 'Florida', TMB8: 'Florida', PBI3: 'Florida',
  JAX3: 'Florida', TPA3: 'Florida', TPA2: 'Florida',
  // West1
  LAS1: 'West1', LAX9: 'West1', VGT2: 'West1', SBD1: 'West1',
  LGB8: 'West1', XLX7: 'West1', ONT8: 'West1', LGB4: 'West1',
  LAS6: 'West1', SNA4: 'West1',
  // West2
  GYR2: 'West2', SCK1: 'West2', SJC7: 'West2', SMF3: 'West2',
  SCK4: 'West2', GEU2: 'West2', SBD2: 'West2', SCK8: 'West2',
  PHX5: 'West2', MCE1: 'West2', PHX3: 'West2', OAK3: 'West2',
  TCY1: 'West2', FAT2: 'West2', RNO4: 'West2', SMF6: 'West2',
  GYR3: 'West2', PHX7: 'West2', MIT2: 'West2', GEU5: 'West2',
  // West3
  ABQ2: 'West3', PDX6: 'West3', BFI9: 'West3', PDX7: 'West3',
  DEN2: 'West3', PSC2: 'West3', GEG2: 'West3', SLC2: 'West3',
  DEN8: 'West3', BFI3: 'West3',
}

// Zone → rate key mapping
const ZONE_TO_RATE_KEY: Record<string, string> = {
  Central: 'C1',
  East1: 'E1',
  East2: 'E2',
  Florida: 'E3',
  West1: 'W1',
  West2: 'W2',
  West3: 'W3',
}

// Oversized FCs (XLFCs) — use oversized rates
const OVERSIZED_FCS = new Set(['AVP9', 'BDL6', 'CLT6', 'HGR2', 'XLX1', 'XLX6', 'XLX7'])

// Hebron, KY rates (USD per cubic foot)
const STANDARD_RATES: Record<string, number> = {
  E1: 1.50, E2: 2.35, E3: 6.99, C1: 2.99, W1: 3.25, W2: 4.15, W3: 4.99,
}

const OVERSIZED_RATES: Record<string, number> = {
  E1: 1.81, E2: 5.31, E3: 5.31, C1: 12.08, W1: 8.60, W2: 8.60, W3: 13.37,
}

export interface PatternEstimate {
  totalCost: number
  zone: string
  rateKey: string
  ratePerCuFt: number
  totalCuFt: number
  totalWeightLbs: number
  totalBoxes: number
  costPerLb: number
  costPerBox: number
  isOversized: boolean
}

interface BoxGroup {
  length_mm?: number
  width_mm?: number
  height_mm?: number
  weight_gm?: number
  number_of_boxes?: number
}

export function calculatePatternEstimate(
  warehouseId: string,
  boxGroups: BoxGroup[]
): PatternEstimate | null {
  const zone = FC_TO_ZONE[warehouseId]
  if (!zone) return null

  const rateKey = ZONE_TO_RATE_KEY[zone]
  if (!rateKey) return null

  const isOversized = OVERSIZED_FCS.has(warehouseId)
  const rates = isOversized ? OVERSIZED_RATES : STANDARD_RATES
  const ratePerCuFt = rates[rateKey]
  if (ratePerCuFt === undefined) return null

  let totalCuFt = 0
  let totalWeightLbs = 0
  let totalBoxes = 0

  for (const group of boxGroups) {
    const numBoxes = group.number_of_boxes || 1
    totalBoxes += numBoxes

    // Convert mm to feet: 1 foot = 304.8mm
    const lengthFt = (group.length_mm || 300) / 304.8
    const widthFt = (group.width_mm || 300) / 304.8
    const heightFt = (group.height_mm || 300) / 304.8
    const cuFtPerBox = lengthFt * widthFt * heightFt
    totalCuFt += cuFtPerBox * numBoxes

    // Convert grams to pounds: 1 lb = 453.592g
    const weightLbsPerBox = (group.weight_gm || 1000) / 453.592
    totalWeightLbs += weightLbsPerBox * numBoxes
  }

  const totalCost = totalCuFt * ratePerCuFt
  const costPerLb = totalWeightLbs > 0 ? totalCost / totalWeightLbs : 0
  const costPerBox = totalBoxes > 0 ? totalCost / totalBoxes : 0

  return {
    totalCost,
    zone,
    rateKey,
    ratePerCuFt,
    totalCuFt,
    totalWeightLbs,
    totalBoxes,
    costPerLb,
    costPerBox,
    isOversized,
  }
}
